const INTRO_UNIT_DAYS = { DAY: 1, WEEK: 7, MONTH: 30, YEAR: 365 }
const ISO_PERIOD_UNIT_DAYS = { D: 1, W: 7, M: 30, Y: 365 }

/**
 * Length in days of the free trial the store attaches to this package, or null
 * when the store reports none (or reports something that is not a plain free
 * trial). The store is the source of truth on native, so no default is assumed.
 * @param {import('@revenuecat/purchases-capacitor').PurchasesPackage | null | undefined} pkg
 * @returns {number | null}
 */
export function freeTrialDaysFromPackage (pkg) {
  const freePhase = pkg?.product?.defaultOption?.freePhase
  if (freePhase && typeof freePhase === 'object') {
    const phaseDays = daysFromGooglePhase(freePhase)
    if (phaseDays !== null) return phaseDays
  }

  const intro = pkg?.product?.introPrice ?? pkg?.product?.introductoryPrice
  if (!intro || typeof intro !== 'object') return null
  if (!(intro.price === 0 || intro.price === '0')) return null

  const cycles = Number(intro.cycles ?? 1) || 1
  const units = Number(intro.periodNumberOfUnits)
  const unitDays = INTRO_UNIT_DAYS[String(intro.periodUnit ?? '').toUpperCase()]
  if (Number.isFinite(units) && units > 0 && unitDays) {
    return units * unitDays * cycles
  }

  const match = /^P(\d+)([DWMY])$/.exec(String(intro.period ?? ''))
  if (match) {
    return Number(match[1]) * ISO_PERIOD_UNIT_DAYS[match[2]] * cycles
  }
  return null
}

/** A store trial is advertised only after RevenueCat confirms it applies. */
export function eligibleFreeTrialDaysFromPackage (pkg, isEligible) {
  return isEligible === true ? freeTrialDaysFromPackage(pkg) : null
}

/** Google Play reports the free trial of the selected offer as a pricing phase. */
function daysFromGooglePhase (phase) {
  const period = phase.billingPeriod
  if (!period || typeof period !== 'object') return null
  const cycles = Number(phase.billingCycleCount ?? 1) || 1
  const value = Number(period.value)
  const unitDays = INTRO_UNIT_DAYS[String(period.unit ?? '').toUpperCase()]
  if (Number.isFinite(value) && value > 0 && unitDays) {
    return value * unitDays * cycles
  }
  const match = /^P(\d+)([DWMY])$/.exec(String(period.iso8601 ?? ''))
  if (match) {
    return Number(match[1]) * ISO_PERIOD_UNIT_DAYS[match[2]] * cycles
  }
  return null
}

/**
 * Whether a completed purchase started as a free trial, read from the purchased
 * entitlement's periodType. An advertised introductory offer is not enough: a
 * returning subscriber is not eligible and is charged immediately. Only an
 * explicit TRIAL counts; anything unknown is treated as not a trial.
 * @param {{ customerInfo?: import('@revenuecat/purchases-capacitor').CustomerInfo, productIdentifier?: string } | null | undefined} result
 * @returns {boolean}
 */
export function purchaseStartedFreeTrial (result) {
  const active = result?.customerInfo?.entitlements?.active
  if (!active || typeof active !== 'object') return false
  const productId = result?.productIdentifier
  const matching = Object.values(active).filter(entitlement => (
    entitlement && typeof entitlement === 'object'
    && (!productId || !entitlement.productIdentifier || entitlement.productIdentifier === productId)
  ))
  return matching.some(entitlement => String(entitlement.periodType).toUpperCase() === 'TRIAL')
}
